import { Check } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import PageHero from "@/components/site/PageHero";
import LeadForm from "@/components/site/LeadForm";
import Seo, { ORG_SCHEMA, serviceSchema, breadcrumbSchema } from "@/components/site/Seo";

const PATHWAYS = [
  { t: "Investors", d: "AED 2M+ qualifying investment in property, business or public funds." },
  { t: "Entrepreneurs", d: "Founders of approved or high-growth UAE-registered businesses." },
  { t: "Specialised Talent", d: "Doctors, scientists, executives, inventors and creatives of distinction." },
  { t: "Property Owners", d: "Owners of UAE real estate valued at AED 2M or above." },
  { t: "Top Students", d: "Outstanding graduates from UAE and approved global universities." },
  { t: "Humanitarian / Pioneers", d: "Awarded individuals in humanitarian or cultural fields." },
];

export default function GoldenVisa() {
  return (
    <div className="bg-white">
      <Seo
        title="UAE Golden Visa — 10-Year Residency"
        description="Apply for the UAE Golden Visa with Velor Advisory. Ten-year, self-sponsored residency for investors, founders and property owners — end to end."
        keywords="UAE Golden Visa, 10 year UAE residency, Golden Visa investors, Golden Visa property owners, Dubai Golden Visa application, UAE self-sponsored residency"
        focusKeyword="UAE Golden Visa"
        path="/golden-visa"
        schema={[
          ORG_SCHEMA,
          serviceSchema({
            name: "UAE Golden Visa Advisory",
            description:
              "End-to-end advisory for the UAE Golden Visa: eligibility assessment, documentation, submission, medical, biometrics and issuance for investors, entrepreneurs, property owners and specialised talent.",
            path: "/golden-visa",
            serviceType: "Immigration and residency advisory",
            offers: {
              "@type": "OfferCatalog",
              name: "Golden Visa Pathways",
              itemListElement: PATHWAYS.map((p) => ({
                "@type": "Offer",
                itemOffered: { "@type": "Service", name: p.t, description: p.d },
              })),
            },
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Golden Visa", path: "/golden-visa" },
          ]),
        ]}
      />
      <PageHero
        overline="UAE Golden Visa"
        title="A decade of UAE residency, on your terms."
        subtitle="Ten-year, self-sponsored residency for investors, founders, entrepreneurs and property owners — guided from eligibility to issuance."
        image="https://images.pexels.com/photos/21282689/pexels-photo-21282689.jpeg?auto=compress&cs=tinysrgb&w=1650&fm=webp"
        imageAlt="Dubai high-rise skyline representing the UAE Golden Visa 10-year residency programme"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-black/45">Why a Golden Visa</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Ten years of stability and <em className="italic">opportunity</em>.</h2>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7 text-black/70 font-light text-lg leading-relaxed space-y-5">
            <p>The UAE Golden Visa is a long-term residency permit valid for ten years, renewable. Unlike a standard residency visa, it is <strong className="font-medium">self-sponsored</strong> — meaning you are not tied to an employer or business.</p>
            <ul className="mt-6 space-y-3 text-base">
              {["Sponsor spouse, children, parents and domestic staff","Multiple entry and exit, with extended stays abroad","100% personal income — UAE has no personal income tax","Access to UAE banking, schooling and healthcare ecosystems"].map((x) => (
                <li key={x} className="flex items-start gap-3"><Check size={16} className="mt-1"/>{x}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#fafafa] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-overline uppercase text-black/45">Pathways</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">Six routes. One trusted process.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10">
            {PATHWAYS.map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) * 80} className="border-r border-b border-black/10 p-8 md:p-10 bg-white">
                <div className="text-[11px] tracking-overline uppercase text-black/45">0{i+1}</div>
                <h3 className="font-serif text-3xl mt-4">{p.t}</h3>
                <p className="mt-3 text-black/60 font-light leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-overline uppercase text-black/45">Process</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 max-w-2xl">Discreet, structured, swift.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-10">
            {[
              { n: "01", t: "Eligibility review", d: "A confidential 30-minute assessment of your profile." },
              { n: "02", t: "Document curation", d: "We prepare, translate and attest every required file." },
              { n: "03", t: "Submission & liaison", d: "We handle submissions and follow-ups with the relevant UAE government authorities directly." },
              { n: "04", t: "Issuance & onboarding", d: "Receive your visa, Emirates ID and family sponsorship." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 80}>
                <div className="font-serif text-5xl text-black/15">{s.n}</div>
                <h3 className="font-serif text-2xl mt-4">{s.t}</h3>
                <p className="mt-3 text-black/60 font-light">{s.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] text-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-white/45">Begin</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Are you eligible? <em className="italic">Let's find out.</em></h2>
            <p className="mt-6 text-white/65 font-light max-w-md">Share a few details and a senior advisor will privately assess your Golden Visa eligibility within 24 hours.</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <div className="bg-white text-black"><LeadForm testid="goldenvisa-lead-form" source="golden-visa" /></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
